import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "cmdk";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Briefcase,
  Clock,
  Contact as ContactIcon,
  GitBranch,
  Lightbulb,
  Library,
  Phone,
  Search as SearchIcon,
  Settings,
  Sparkles,
  Trophy,
  UserCheck,
  Users,
  Workflow,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useSearch } from "@/modules/sales/search/hooks/useSearch";
import { useRecentSearchesStore } from "@/modules/sales/search/stores/recentSearches.store";
import type {
  SearchResultItem,
  SearchSourceType,
} from "@/modules/sales/search/types";

/**
 * CommandPalette — global Cmd+K dialog.
 *
 * Funksiyalar:
 *   - Live search (debounced 300ms) — `useSearch` hook
 *   - Quick navigate — page URL'lar (Sales sahifalari)
 *   - Recent searches (Zustand cache)
 *   - Result groups: Pages / Calls / Leads / Contacts
 *   - Click → tegishli sahifa yoki to'liq qidiruv
 *   - Keyboard: ↑/↓ Enter, Esc yopish
 *
 * `cmdk` paket — keyboard nav o'zining ichida.
 *
 * Accessibility:
 *   - DialogTitle visually-hidden (Radix talabi)
 *   - role="combobox" cmdk ichida
 */

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface QuickPage {
  to: string;
  label: string;
  Icon: typeof SearchIcon;
  keywords?: string;
}

const QUICK_PAGES: QuickPage[] = [
  { to: "/sales/dashboard", label: "Sales Dashboard", Icon: BarChart3, keywords: "dashboard kpi" },
  { to: "/sales/leads", label: "Leads", Icon: Users, keywords: "lead" },
  { to: "/sales/contacts", label: "Kontaktlar", Icon: ContactIcon, keywords: "contact" },
  { to: "/sales/pipelines", label: "Pipelines", Icon: Workflow, keywords: "pipeline funnel" },
  { to: "/sales/deals", label: "Deals", Icon: Briefcase, keywords: "deal sotuv" },
  { to: "/sales/calls", label: "Qo'ng'iroqlar", Icon: Phone, keywords: "calls audio qongiroq" },
  { to: "/sales/custdev", label: "CustDev", Icon: Lightbulb, keywords: "custdev mijoz" },
  { to: "/sales/managers", label: "Menejerlar", Icon: UserCheck, keywords: "manager" },
  { to: "/sales/leaderboard", label: "Leaderboard", Icon: Trophy, keywords: "rating leaderboard" },
  { to: "/sales/voronka-audit", label: "Voronka audit", Icon: GitBranch, keywords: "voronka funnel" },
  { to: "/sales/playbook", label: "Playbook", Icon: BookOpen, keywords: "playbook texnika" },
  { to: "/sales/knowledge", label: "Bilim bazasi", Icon: Library, keywords: "knowledge document" },
  { to: "/sales/search", label: "Universal qidiruv", Icon: SearchIcon, keywords: "search qidiruv" },
  { to: "/settings", label: "Sozlamalar", Icon: Settings, keywords: "settings sozlama" },
];

const TYPE_META: Record<
  SearchSourceType,
  { Icon: typeof Phone; label: string; href: (id: string) => string }
> = {
  call: { Icon: Phone, label: "Qo'ng'iroq", href: (id) => `/sales/calls/${id}` },
  lead: { Icon: Users, label: "Lead", href: (id) => `/sales/leads/${id}` },
  contact: { Icon: ContactIcon, label: "Kontakt", href: (id) => `/sales/contacts/${id}` },
};

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const recents = useRecentSearchesStore((s) => s.items);
  const pushRecent = useRecentSearchesStore((s) => s.push);
  const removeRecent = useRecentSearchesStore((s) => s.remove);

  // Dialog yopilganda query tozalanadi
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const { data, isFetching } = useSearch(
    { query, type: "all", limit: 12 },
    { debounceMs: 250, minLength: 2, enabled: open },
  );

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const grouped = useMemo(() => {
    const map: Record<SearchSourceType, SearchResultItem[]> = {
      call: [],
      lead: [],
      contact: [],
    };
    for (const it of items) map[it.type]?.push(it);
    return map;
  }, [items]);

  const close = () => onOpenChange(false);

  const goToFullSearch = (q?: string) => {
    const v = (q ?? query).trim();
    if (v) pushRecent(v, "all");
    navigate(`/sales/search${v ? `?q=${encodeURIComponent(v)}` : ""}`);
    close();
  };

  const goToResult = (it: SearchResultItem) => {
    if (query.trim()) pushRecent(query.trim(), "all");
    navigate(TYPE_META[it.type].href(it.id));
    close();
  };

  const goToPage = (to: string) => {
    navigate(to);
    close();
  };

  const showResults = query.trim().length >= 2;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[var(--z-overlay)] bg-[var(--bg-overlay)] backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-[15%] z-[var(--z-modal)] w-[92vw] max-w-[640px] -translate-x-1/2",
            "rounded-xl bg-[var(--bg-elevated)] border border-[var(--border-subtle)]",
            "shadow-[var(--shadow-modal)] outline-none focus:outline-none focus-visible:outline-none",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          )}
          aria-label="Buyruqlar paneli"
        >
          <DialogPrimitive.Title className="sr-only">
            Buyruqlar paneli
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Sahifa yoki yozuvlar bo'yicha tezkor qidiruv
          </DialogPrimitive.Description>

          <Command
            label="Buyruqlar paneli"
            shouldFilter={!showResults}
            className="flex w-full flex-col"
          >
            {/* Input */}
            <div className="flex items-center gap-2 border-b border-[var(--border-subtle)] px-3.5 transition-colors focus-within:border-[var(--brand-primary)]">
              <SearchIcon
                className={cn("h-4 w-4 shrink-0", isFetching && "animate-pulse")}
                style={{
                  color: isFetching
                    ? "var(--brand-primary)"
                    : "var(--text-muted)",
                }}
                aria-hidden
              />
              <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder="Qidirish yoki sahifaga o'tish..."
                className="h-12 flex-1 bg-transparent text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && query.trim().length >= 2 && items.length === 0) {
                    e.preventDefault();
                    goToFullSearch();
                  }
                }}
              />
              <kbd className="hidden md:inline-flex items-center gap-0.5 rounded border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[11px] font-mono text-[var(--text-muted)]">
                Esc
              </kbd>
              <button
                type="button"
                onClick={close}
                aria-label="Yopish"
                className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>

            <CommandList className="max-h-[420px] overflow-y-auto p-1.5">
              {/* Empty: cmdk default fallback */}
              <CommandEmpty className="px-3 py-6 text-center text-sm text-[var(--text-muted)]">
                {showResults && isFetching ? (
                  <span className="inline-flex items-center gap-2">
                    <Sparkles
                      className="h-3 w-3 animate-pulse"
                      style={{ color: "var(--brand-primary)" }}
                    />
                    Qidirilmoqda...
                  </span>
                ) : showResults ? (
                  <>
                    Hech narsa topilmadi.{" "}
                    <button
                      type="button"
                      onClick={() => goToFullSearch()}
                      className="underline-offset-2 hover:underline"
                      style={{ color: "var(--brand-primary)" }}
                    >
                      To'liq qidiruvda ko'rish
                    </button>
                  </>
                ) : (
                  "Yozishni boshlang yoki sahifani tanlang"
                )}
              </CommandEmpty>

              {/* AI command — quick-launch (only when no query) */}
              {!showResults && (
                <>
                  <CommandGroup
                    heading="AI yordamchisi"
                    className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
                  >
                    <CommandItem
                      value="ai ask sparkles"
                      onSelect={() => {
                        close();
                        window.dispatchEvent(new CustomEvent("ai-panel:open"));
                      }}
                      className={paletteItemClass}
                    >
                      <Sparkles
                        className="h-3.5 w-3.5 shrink-0"
                        style={{ color: "var(--brand-primary)" }}
                        aria-hidden
                      />
                      <span className="flex-1 truncate">AI yordamchidan so'rang</span>
                      <kbd className="hidden md:inline-flex items-center gap-0.5 rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-1 py-0.5 text-[11px] font-mono text-[var(--text-muted)]">
                        ⌘ J
                      </kbd>
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator className="my-1 h-px bg-[var(--border-subtle)]" />
                </>
              )}

              {/* Recent searches (only when no query) */}
              {!showResults && recents.length > 0 && (
                <>
                  <CommandGroup
                    heading="So'nggi qidiruvlar"
                    className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
                  >
                    {recents.slice(0, 6).map((r) => (
                      <CommandItem
                        key={`recent-${r.query}`}
                        value={`recent ${r.query}`}
                        onSelect={() => goToFullSearch(r.query)}
                        className={paletteItemClass}
                      >
                        <Clock
                          className="h-3.5 w-3.5 shrink-0"
                          style={{ color: "var(--text-muted)" }}
                          aria-hidden
                        />
                        <span className="flex-1 truncate">{r.query}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRecent(r.query);
                          }}
                          className="opacity-0 transition-opacity group-hover:opacity-100"
                          aria-label="O'chirish"
                        >
                          <X className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)]" />
                        </button>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator className="my-1 h-px bg-[var(--border-subtle)]" />
                </>
              )}

              {/* Pages — always visible (filtered by cmdk when no live results) */}
              {!showResults && (
                <CommandGroup
                  heading="Sahifalar"
                  className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
                >
                  {QUICK_PAGES.map((p) => {
                    const Icon = p.Icon;
                    return (
                      <CommandItem
                        key={p.to}
                        value={`${p.label} ${p.keywords ?? ""}`}
                        onSelect={() => goToPage(p.to)}
                        className={paletteItemClass}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0 text-[var(--text-muted)]" aria-hidden />
                        <span className="flex-1 truncate">{p.label}</span>
                        <ArrowRight className="h-3 w-3 text-[var(--text-muted)] opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}

              {/* Live results — calls / leads / contacts */}
              {showResults && (
                <>
                  {(["call", "lead", "contact"] as SearchSourceType[]).map((tp) => {
                    const list = grouped[tp];
                    if (!list?.length) return null;
                    const meta = TYPE_META[tp];
                    return (
                      <CommandGroup
                        key={tp}
                        heading={meta.label + ` (${list.length})`}
                        className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
                      >
                        {list.slice(0, 5).map((it) => {
                          const Icon = meta.Icon;
                          return (
                            <CommandItem
                              key={`${tp}-${it.id}`}
                              value={`${tp} ${it.title} ${it.id}`}
                              onSelect={() => goToResult(it)}
                              className={paletteItemClass}
                            >
                              <Icon
                                className="h-3.5 w-3.5 shrink-0"
                                style={{ color: "var(--brand-primary)" }}
                                aria-hidden
                              />
                              <div className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate text-sm text-[var(--text-primary)]">
                                  {it.title}
                                </span>
                                <span
                                  className="truncate text-[11px] text-[var(--text-muted)]
                                             [&_mark]:bg-[var(--brand-soft)] [&_mark]:text-[var(--text-primary)] [&_mark]:rounded [&_mark]:px-0.5"
                                  dangerouslySetInnerHTML={{ __html: it.snippet }}
                                />
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    );
                  })}

                  {items.length > 0 && (
                    <>
                      <CommandSeparator className="my-1 h-px bg-[var(--border-subtle)]" />
                      <CommandGroup>
                        <CommandItem
                          value={`__open_full__ ${query}`}
                          onSelect={() => goToFullSearch()}
                          className={paletteItemClass}
                        >
                          <SearchIcon
                            className="h-3.5 w-3.5 shrink-0"
                            style={{ color: "var(--brand-primary)" }}
                            aria-hidden
                          />
                          <span className="flex-1 text-sm">
                            "{query}" — to'liq qidiruv natijalarini ko'rish
                          </span>
                          <ArrowRight className="h-3 w-3 text-[var(--text-muted)]" aria-hidden />
                        </CommandItem>
                      </CommandGroup>
                    </>
                  )}
                </>
              )}
            </CommandList>

            {/* Footer hint */}
            <div className="flex items-center gap-3 border-t border-[var(--border-subtle)] px-3 py-2 text-[10px] text-[var(--text-muted)]">
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-1 py-0.5 text-[11px] font-mono">↑↓</kbd>
                yurish
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-1 py-0.5 text-[11px] font-mono">Enter</kbd>
                tanlash
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-1 py-0.5 text-[11px] font-mono">Esc</kbd>
                yopish
              </span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const paletteItemClass = cn(
  "group flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm",
  "text-[var(--text-secondary)]",
  "data-[selected=true]:bg-[var(--bg-elevated)] data-[selected=true]:text-[var(--text-primary)]",
  "aria-selected:bg-[var(--bg-elevated)] aria-selected:text-[var(--text-primary)]",
);
